import { useState } from "react";
import { Mail, MapPin, Phone, Twitter, Github, Linkedin, Instagram } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const submit = (e) => {
    e.preventDefault();
    toast.success("Message sent!", { description: "We'll reply within 1 business day." });
    setForm({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <div data-testid="contact-page">
      <section className="border-b border-warm-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <span className="text-sm font-medium text-saffron-600">Contact</span>
            <h1 className="mt-2 font-display text-5xl md:text-6xl font-semibold tracking-tight text-navy-800">Let's talk.</h1>
            <p className="mt-4 text-lg text-navy-600">Whether you're launching your first campaign or evaluating Dwinov for your studio, we'd love to hear from you.</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-2xl border border-warm-200 bg-white p-8">
              <div className="inline-grid place-items-center h-11 w-11 rounded-xl bg-saffron-50 text-saffron-600 mb-4"><Mail className="h-5 w-5" /></div>
              <h3 className="font-display text-lg font-semibold text-navy-800">Email us</h3>
              <p className="mt-1 text-sm text-navy-600">The fastest way to reach our team.</p>
              <a href="mailto:hello@dwinov.com" className="mt-3 inline-block text-saffron-600 font-medium hover:underline">hello@dwinov.com</a>
            </div>
            <div className="rounded-2xl border border-warm-200 bg-white p-8">
              <div className="inline-grid place-items-center h-11 w-11 rounded-xl bg-saffron-50 text-saffron-600 mb-4"><MapPin className="h-5 w-5" /></div>
              <h3 className="font-display text-lg font-semibold text-navy-800">Visit us</h3>
              <p className="mt-1 text-sm text-navy-600">Dwinov HQ</p>
              <p className="text-sm text-navy-600">548 Market St · Suite 4200<br />San Francisco, CA 94104</p>
            </div>
            <div className="rounded-2xl border border-warm-200 bg-white p-8">
              <div className="inline-grid place-items-center h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 mb-4"><Phone className="h-5 w-5" /></div>
              <h3 className="font-display text-lg font-semibold text-navy-800">Follow along</h3>
              <div className="mt-3 flex gap-2">
                {[Twitter, Github, Linkedin, Instagram].map((I, i) => (
                  <a key={i} href="#" className="grid place-items-center h-10 w-10 rounded-xl border border-warm-200 text-navy-600 hover:text-saffron-600 hover:border-saffron-200 transition-colors" aria-label="Social link">
                    <I className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 rounded-2xl border border-warm-200 bg-white p-8" data-testid="contact-form">
            <h2 className="font-display text-2xl font-semibold text-navy-800">Send us a message</h2>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-12 rounded-2xl border-warm-200" data-testid="contact-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 h-12 rounded-2xl border-warm-200" data-testid="contact-email" />
              </div>
            </div>
            <div className="mt-4">
              <Label>Subject</Label>
              <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-2 h-12 rounded-2xl border-warm-200" data-testid="contact-subject" />
            </div>
            <div className="mt-4">
              <Label>Message</Label>
              <Textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 rounded-2xl border-warm-200 min-h-[160px]" data-testid="contact-message" />
            </div>
            <Button type="submit" data-testid="contact-submit" className="mt-6 rounded-2xl bg-saffron-600 hover:bg-saffron-700 h-12 px-6 hover:scale-[1.02] active:scale-95 transition-all">Send message</Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
